import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { S3ViewerPage } from '@spreadshirt/backstage-plugin-s3-viewer';

import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import { AlertDisplay, OAuthRequestDialog, SignInPage} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { githubAuthApiRef, gitlabAuthApiRef, configApiRef, useApi } from "@backstage/core-plugin-api";
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { HomePage } from './components/home/HomePage';
import { AnnouncementsPage } from '@backstage-community/plugin-announcements';
import { ExplorePage } from '@backstage-community/plugin-explore';
import { explorePage } from './components/explore/ExplorePage';
import { CostInsightsPage } from '@backstage-community/plugin-cost-insights';
import { apis, oidcAuthApiRef } from './apis';
import { RbacPage } from '@backstage-community/plugin-rbac';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { VisitListener } from '@backstage/plugin-home';
import { kubriXTheme } from './theme/kubrix';

import { UnifiedThemeProvider, themes } from '@backstage/theme';

import { McpChatPage } from '@backstage-community/plugin-mcp-chat';

const app = createApp({
  apis,
  components: {
    SignInPage: props => {
      const configApi = useApi(configApiRef);

      const rawProviders =
        configApi.getOptionalStringArray('kubrix.auth.allowedProviders');

      // Debug-Log
      // eslint-disable-next-line no-console
      console.log('kubrix.auth.allowedProviders:', rawProviders);

      if (!rawProviders) {
        // eslint-disable-next-line no-console
        console.warn(
          'No kubrix.auth.allowedProviders configured. Only OIDC will be shown.',
        );
      }

      const allowedProviders = rawProviders ?? [];

      const knownProviders = ['github', 'gitlab'];
      const unknownProviders = allowedProviders.filter(
        p => !knownProviders.includes(p),
      );

      if (unknownProviders.length > 0) {
        // eslint-disable-next-line no-console
        console.warn(
          `Unknown sign-in providers in kubrix.auth.allowedProviders: ${unknownProviders.join(
            ', ',
          )}. Supported: ${knownProviders.join(', ')}`,
        );
      }

      const providers: any[] = [
        {
          id: 'oidc',
          title: 'Keycloak OIDC',
          message: 'Sign in with Keycloak',
          apiRef: oidcAuthApiRef,
        },
      ];

      if (allowedProviders.includes('github')) {
        providers.push({
          id: 'github-auth-provider',
          title: 'GitHub',
          message: 'Sign in using GitHub',
          apiRef: githubAuthApiRef,
        });
      }

      if (allowedProviders.includes('gitlab')) {
        providers.push({
          id: 'gitlab-auth-provider',
          title: 'Gitlab',
          message: 'Sign in using Gitlab',
          apiRef: gitlabAuthApiRef,
        });
      }

      return <SignInPage {...props} auto providers={providers} />;
    },
  },
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  themes: [
    {
      id: 'kubrix',
      title: 'kubriX',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={kubriXTheme} children={children} />

      ),
    },
    {
      id: 'light',
      title: 'Light',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={themes.light} children={children} />
      ),
    },
    {
      id: 'dark',
      title: 'Dark',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={themes.dark} children={children} />
      ),
    },

  ],
});

const routes = (
  <FlatRoutes>

    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
        <Route path="/explore" element={<ExplorePage />}>
      {explorePage}
    </Route>

    <Route path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
      />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/announcements" element={<AnnouncementsPage />} />
    <Route path="/cost-insights" element={<CostInsightsPage />} />
    <Route path="/rbac" element={<RbacPage />} />;
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/mcp-chat" element={<McpChatPage />} />;
    <Route path="/s3-viewer" element={<S3ViewerPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <VisitListener />
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
